/**
 * Pomodoro Service
 *
 * Manages the Pomodoro timer lifecycle: start, pause, resume, complete, cancel.
 * Active sessions are held in memory. Only completed/cancelled sessions are
 * persisted to IndexedDB and enqueued for server sync.
 *
 * @module pomodoroService
 * @see Requirements 7.1, 7.2, 7.4, 7.5, 7.6, 12.3
 */

import { v4 as uuidv4 } from 'uuid';
import type { PomodoroSession, PomodoroConfig, PomodoroStats } from '../types';
import { SyncStatus } from '../types/enums';
import { db } from './localDb';
import { eventBus } from './eventBus';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Default Pomodoro configuration. */
const DEFAULT_CONFIG: PomodoroConfig = {
  workDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  sessionsBeforeLongBreak: 4,
};

/** Key used to store Pomodoro config in appSettings. */
const CONFIG_KEY = 'pomodoroConfig';

// ─── PomodoroService Interface ───────────────────────────────────────────────

export interface PomodoroServiceInterface {
  startSession(config: {
    taskId?: string;
    phase: 'work' | 'shortBreak' | 'longBreak';
    duration: number;
  }): PomodoroSession;
  pauseSession(sessionId: string): PomodoroSession;
  resumeSession(sessionId: string): PomodoroSession;
  completeSession(sessionId: string): Promise<PomodoroSession>;
  cancelSession(sessionId: string): Promise<PomodoroSession>;

  getActiveSession(): PomodoroSession | null;
  getSessionsByDate(date: string): Promise<PomodoroSession[]>;
  getSessionsByTask(taskId: string): Promise<PomodoroSession[]>;
  getDailyStats(date: string): Promise<PomodoroStats>;

  getConfig(): PomodoroConfig;
  updateConfig(config: Partial<PomodoroConfig>): Promise<void>;
}

// ─── PomodoroService Implementation ─────────────────────────────────────────

/**
 * Creates and returns a PomodoroService instance.
 *
 * Active session state is managed in memory (not persisted until completed).
 * Only completed sessions are written to IndexedDB and synced.
 * start/pause/resume are synchronous (state machine transitions).
 * complete/cancel are async (write to DB).
 */
export function createPomodoroService(): PomodoroServiceInterface {
  /** Active session held in memory. */
  let activeSession: PomodoroSession | null = null;

  /** Timestamp of the last resume or start (for actualDuration tracking). */
  let lastResumedAt: number | null = null;

  /** Accumulated duration in seconds before the current running interval. */
  let accumulatedSeconds = 0;

  /** In-memory config (loaded from DB on first access via loadConfig). */
  let currentConfig: PomodoroConfig = { ...DEFAULT_CONFIG };

  // ─── Helper Functions ───────────────────────────────────────────────────

  /**
   * Load config from IndexedDB appSettings store.
   */
  async function loadConfig(): Promise<void> {
    const setting = await db.appSettings.get(CONFIG_KEY);
    if (setting && setting.value) {
      currentConfig = { ...DEFAULT_CONFIG, ...(setting.value as Partial<PomodoroConfig>) };
    }
  }

  /**
   * Calculate the current actualDuration based on accumulated time
   * plus time elapsed since last resume.
   */
  function calculateActualDuration(): number {
    if (lastResumedAt === null) {
      return accumulatedSeconds;
    }
    const elapsed = Math.floor((Date.now() - lastResumedAt) / 1000);
    return accumulatedSeconds + elapsed;
  }

  /**
   * Enqueue a completed/cancelled session to the sync queue.
   */
  async function enqueueSync(session: PomodoroSession): Promise<void> {
    const size = await db.syncQueue.count();
    if (size >= 1000) {
      // Queue full — skip sync but still persist locally
      return;
    }
    await db.syncQueue.add({
      module: 'pomodoro',
      entityId: session.sessionId,
      action: 'create',
      timestamp: new Date().toISOString(),
      payload: session as unknown as Record<string, unknown>,
      retryCount: 0,
    } as never);
  }

  /**
   * Update the linked task's focusMinutes when a work session completes.
   */
  async function updateTaskFocusMinutes(
    taskId: string,
    durationSeconds: number
  ): Promise<void> {
    const task = await db.tasks.get(taskId);
    if (task) {
      const additionalMinutes = Math.round(durationSeconds / 60);
      await db.tasks.update(taskId, {
        focusMinutes: task.focusMinutes + additionalMinutes,
        updatedAt: new Date().toISOString(),
      });
    }
  }

  // ─── Public Methods ─────────────────────────────────────────────────────

  function startSession(config: {
    taskId?: string;
    phase: 'work' | 'shortBreak' | 'longBreak';
    duration: number;
  }): PomodoroSession {
    // Cannot start if a session is already active
    if (activeSession && (activeSession.status === 'active' || activeSession.status === 'paused')) {
      throw new Error('A Pomodoro session is already active. Complete or cancel it first.');
    }

    const now = new Date().toISOString();
    const session: PomodoroSession = {
      sessionId: uuidv4(),
      userId: '', // Will be set by the store/caller context
      taskId: config.taskId ?? null,
      startedAt: now,
      completedAt: null,
      duration: config.duration,
      actualDuration: 0,
      phase: config.phase,
      status: 'active',
      createdAt: now,
      syncStatus: SyncStatus.Pending,
    };

    activeSession = session;
    lastResumedAt = Date.now();
    accumulatedSeconds = 0;

    return { ...session };
  }

  function pauseSession(sessionId: string): PomodoroSession {
    if (!activeSession || activeSession.sessionId !== sessionId) {
      throw new Error('No active session found with the given ID.');
    }
    if (activeSession.status !== 'active') {
      throw new Error('Session is not active. Cannot pause.');
    }

    // Accumulate elapsed time since last resume
    if (lastResumedAt !== null) {
      accumulatedSeconds += Math.floor((Date.now() - lastResumedAt) / 1000);
      lastResumedAt = null;
    }

    activeSession = {
      ...activeSession,
      status: 'paused',
      actualDuration: accumulatedSeconds,
    };

    return { ...activeSession };
  }

  function resumeSession(sessionId: string): PomodoroSession {
    if (!activeSession || activeSession.sessionId !== sessionId) {
      throw new Error('No active session found with the given ID.');
    }
    if (activeSession.status !== 'paused') {
      throw new Error('Session is not paused. Cannot resume.');
    }

    lastResumedAt = Date.now();

    activeSession = {
      ...activeSession,
      status: 'active',
    };

    return { ...activeSession };
  }

  async function completeSession(sessionId: string): Promise<PomodoroSession> {
    if (!activeSession || activeSession.sessionId !== sessionId) {
      throw new Error('No active session found with the given ID.');
    }
    if (activeSession.status !== 'active' && activeSession.status !== 'paused') {
      throw new Error('Session cannot be completed in its current state.');
    }

    const now = new Date().toISOString();
    const actualDuration = calculateActualDuration();

    const completedSession: PomodoroSession = {
      ...activeSession,
      status: 'completed',
      completedAt: now,
      actualDuration,
    };

    // Persist to IndexedDB
    await db.pomodoroSessions.put(completedSession);

    // Enqueue for server sync
    await enqueueSync(completedSession);

    // Update linked task's focusMinutes for work sessions
    if (completedSession.phase === 'work' && completedSession.taskId) {
      await updateTaskFocusMinutes(completedSession.taskId, actualDuration);
    }

    // Emit event via event bus
    eventBus.emit('pomodoro:session-complete', {
      taskId: completedSession.taskId,
      duration: actualDuration,
    });

    // Clear active session state
    activeSession = null;
    lastResumedAt = null;
    accumulatedSeconds = 0;

    return { ...completedSession };
  }

  async function cancelSession(sessionId: string): Promise<PomodoroSession> {
    if (!activeSession || activeSession.sessionId !== sessionId) {
      throw new Error('No active session found with the given ID.');
    }
    if (activeSession.status !== 'active' && activeSession.status !== 'paused') {
      throw new Error('Session cannot be cancelled in its current state.');
    }

    const now = new Date().toISOString();
    const actualDuration = calculateActualDuration();

    const cancelledSession: PomodoroSession = {
      ...activeSession,
      status: 'cancelled',
      completedAt: now,
      actualDuration,
    };

    // Persist cancelled sessions to IndexedDB for record-keeping
    await db.pomodoroSessions.put(cancelledSession);

    // Enqueue for server sync
    await enqueueSync(cancelledSession);

    // Clear active session state
    activeSession = null;
    lastResumedAt = null;
    accumulatedSeconds = 0;

    return { ...cancelledSession };
  }

  function getActiveSession(): PomodoroSession | null {
    if (!activeSession) return null;

    // Return a snapshot with current actualDuration
    if (activeSession.status === 'active') {
      return {
        ...activeSession,
        actualDuration: calculateActualDuration(),
      };
    }
    return { ...activeSession };
  }

  async function getSessionsByDate(date: string): Promise<PomodoroSession[]> {
    // Query sessions whose startedAt begins with the given date (YYYY-MM-DD)
    const allSessions = await db.pomodoroSessions
      .where('startedAt')
      .between(date + 'T00:00:00.000Z', date + 'T23:59:59.999Z', true, true)
      .toArray();

    return allSessions;
  }

  async function getSessionsByTask(taskId: string): Promise<PomodoroSession[]> {
    return db.pomodoroSessions
      .where('taskId')
      .equals(taskId)
      .toArray();
  }

  async function getDailyStats(date: string): Promise<PomodoroStats> {
    const sessions = await getSessionsByDate(date);

    // Only count completed sessions for stats
    const completedSessions = sessions.filter(s => s.status === 'completed');

    let totalFocusMinutes = 0;
    const sessionsByTask: Record<string, number> = {};

    for (const session of completedSessions) {
      // Only work sessions contribute to focus minutes
      if (session.phase === 'work') {
        totalFocusMinutes += Math.round(session.actualDuration / 60);
      }

      // All completed sessions count toward per-task tracking
      if (session.taskId) {
        sessionsByTask[session.taskId] = (sessionsByTask[session.taskId] || 0) + 1;
      }
    }

    return {
      date,
      totalSessions: completedSessions.length,
      totalFocusMinutes,
      sessionsByTask,
    };
  }

  function getConfig(): PomodoroConfig {
    return { ...currentConfig };
  }

  async function updateConfig(config: Partial<PomodoroConfig>): Promise<void> {
    // Validate duration bounds (1–120 minutes)
    if (config.workDuration !== undefined) {
      if (config.workDuration < 1 || config.workDuration > 120) {
        throw new Error('Work duration must be between 1 and 120 minutes.');
      }
    }
    if (config.shortBreakDuration !== undefined) {
      if (config.shortBreakDuration < 1 || config.shortBreakDuration > 120) {
        throw new Error('Short break duration must be between 1 and 120 minutes.');
      }
    }
    if (config.longBreakDuration !== undefined) {
      if (config.longBreakDuration < 1 || config.longBreakDuration > 120) {
        throw new Error('Long break duration must be between 1 and 120 minutes.');
      }
    }
    if (config.sessionsBeforeLongBreak !== undefined) {
      if (config.sessionsBeforeLongBreak < 1) {
        throw new Error('Sessions before long break must be at least 1.');
      }
    }

    currentConfig = { ...currentConfig, ...config };

    // Persist config to IndexedDB appSettings store
    await db.appSettings.put({ key: CONFIG_KEY, value: currentConfig });
  }

  // ─── Initialize Config ──────────────────────────────────────────────────

  // Load config asynchronously on creation (non-blocking)
  void loadConfig();

  return {
    startSession,
    pauseSession,
    resumeSession,
    completeSession,
    cancelSession,
    getActiveSession,
    getSessionsByDate,
    getSessionsByTask,
    getDailyStats,
    getConfig,
    updateConfig,
  };
}

/** Singleton Pomodoro service instance. */
export const pomodoroService = createPomodoroService();
